/**
 * RichTextEditor — the event introduction's editor on Edit Event: formatted text and images.
 *
 * Tiptap (ProseMirror) headless, with a toolbar of our own in Cloudscape's spacing rather than a
 * themed one. What it can produce is exactly what the service keeps (AWSECEventHub
 * service/EventDescriptionHtml): headings 2–3, bold, italic, underline, strike, bullet and numbered
 * lists, blockquotes, links and images. Code, code blocks and horizontal rules are switched off
 * because the service would drop them on save, and a format that vanishes on save is worse than one
 * that is not offered.
 *
 * Images: the toolbar's Image button picks a file, checks it against the service's rules
 * (utils/eventDescription `checkImage`), and hands it to `onUploadImage`, which uploads it and
 * resolves to the image id. Only then is it inserted — shown from a local object URL, and carrying
 * the id as `data-file-id`, which is what is saved. An image therefore never sits in the editor
 * without an upload behind it. Dragging a corner of a selected image resizes it, aspect ratio kept;
 * the size is saved with it as `width` / `height`.
 *
 * The local preview URLs of inserted images are revoked when the editor goes away; the saved form
 * never holds them (utils/eventDescription `descriptionForSave` drops every `src`).
 *
 * Uncontrolled after mount: `value` seeds it and `onChange` reports every edit as HTML. Re-seeding
 * on each change would reset the cursor on every keystroke.
 */
import { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Input from '@amzn/awsui-components-react/polaris/input';
import Modal from '@amzn/awsui-components-react/polaris/modal';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Spinner from '@amzn/awsui-components-react/polaris/spinner';
import { IMAGE_ACCEPT, checkImage } from '../../utils/eventDescription';
import '../EventDescriptionView/EventDescriptionView.css';
import './RichTextEditor.css';

/** The image node, plus the id of the upload it shows — rendered as `data-file-id`. */
const EventImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      imageId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-file-id'),
        renderHTML: (attrs) => (attrs.imageId ? { 'data-file-id': attrs.imageId } : {}),
      },
    };
  },
});

export interface RichTextEditorProps {
  /** The initial HTML (the stored description, with presigned image URLs). */
  value: string;
  /** Every edit, as HTML. */
  onChange: (html: string) => void;
  /** Uploads an image and resolves to its id. */
  onUploadImage: (file: File) => Promise<string>;
  ariaLabel?: string;
}

interface ToolProps {
  label: string;
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function Tool({ label, title, active, disabled, onClick }: ToolProps) {
  return (
    <button
      type="button"
      className={`eh-rte__tool${active ? ' eh-rte__tool--active' : ''}`}
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      // Keep the editor's selection: a mousedown on the button would otherwise blur it.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function Toolbar({
  editor,
  uploading,
  onImage,
  onLink,
}: {
  editor: Editor;
  uploading: boolean;
  onImage: () => void;
  onLink: () => void;
}) {
  const chain = () => editor.chain().focus();
  return (
    <div className="eh-rte__toolbar" role="toolbar" aria-label="Formatting">
      <Tool label="H2" title="Heading" active={editor.isActive('heading', { level: 2 })}
        onClick={() => chain().toggleHeading({ level: 2 }).run()} />
      <Tool label="H3" title="Subheading" active={editor.isActive('heading', { level: 3 })}
        onClick={() => chain().toggleHeading({ level: 3 }).run()} />
      <span className="eh-rte__sep" />
      <Tool label="B" title="Bold" active={editor.isActive('bold')} onClick={() => chain().toggleBold().run()} />
      <Tool label="I" title="Italic" active={editor.isActive('italic')} onClick={() => chain().toggleItalic().run()} />
      <Tool label="U" title="Underline" active={editor.isActive('underline')}
        onClick={() => chain().toggleUnderline().run()} />
      <Tool label="S" title="Strikethrough" active={editor.isActive('strike')}
        onClick={() => chain().toggleStrike().run()} />
      <span className="eh-rte__sep" />
      <Tool label="• List" title="Bulleted list" active={editor.isActive('bulletList')}
        onClick={() => chain().toggleBulletList().run()} />
      <Tool label="1. List" title="Numbered list" active={editor.isActive('orderedList')}
        onClick={() => chain().toggleOrderedList().run()} />
      <Tool label="❝ Quote" title="Quote" active={editor.isActive('blockquote')}
        onClick={() => chain().toggleBlockquote().run()} />
      <span className="eh-rte__sep" />
      <Tool label="Link" title="Link" active={editor.isActive('link')} onClick={onLink} />
      <Tool label={uploading ? 'Uploading…' : 'Image'} title="Insert image" disabled={uploading} onClick={onImage} />
      {uploading ? <Spinner /> : null}
    </div>
  );
}

/** An http(s) or mailto link as typed, or undefined when it is not one the service keeps. */
function linkHref(typed: string): string | undefined {
  const t = typed.trim();
  if (!t) return undefined;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(t) ? t : `https://${t}`;
  return /^(https?:|mailto:)/i.test(withScheme) ? withScheme : undefined;
}

export default function RichTextEditor({ value, onChange, onUploadImage, ariaLabel }: RichTextEditorProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const previews = useRef<string[]>([]);
  useEffect(() => () => previews.current.forEach((url) => URL.revokeObjectURL(url)), []);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        horizontalRule: false,
        link: {
          openOnClick: false,
          autolink: true,
          protocols: ['http', 'https', 'mailto'],
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer nofollow' },
        },
      }),
      // Corner handles, aspect ratio kept; the size is saved as the image's width / height, which the
      // service keeps (whole pixels) and the page caps at its own width.
      EventImage.configure({
        inline: false,
        resize: {
          enabled: true,
          directions: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
          minWidth: 48,
          minHeight: 48,
          alwaysPreserveAspectRatio: true,
        },
      }),
    ],
    content: value,
    // The toolbar's pressed state follows the selection.
    shouldRerenderOnTransaction: true,
    editorProps: { attributes: { class: 'eh-rte__content', 'aria-label': ariaLabel ?? 'Rich text' } },
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  async function insertImage(file: File) {
    if (!editor) return;
    const check = checkImage(file);
    if (!check.ok) {
      setError(check.reason);
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const imageId = await onUploadImage(file);
      const preview = URL.createObjectURL(file);
      previews.current.push(preview);
      editor.chain().focus().insertContent({ type: 'image', attrs: { src: preview, imageId } }).run();
    } catch (e: unknown) {
      setError(`The image could not be uploaded: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setUploading(false);
    }
  }

  function openLink() {
    if (!editor) return;
    setLinkText((editor.getAttributes('link').href as string | undefined) ?? '');
    setLinkOpen(true);
  }

  function applyLink() {
    if (!editor) return;
    const href = linkHref(linkText);
    const chain = editor.chain().focus().extendMarkRange('link');
    (href ? chain.setLink({ href }) : chain.unsetLink()).run();
    setLinkOpen(false);
  }

  if (!editor) return null;

  return (
    <SpaceBetween size="xs">
      <div className="eh-rte">
        <Toolbar editor={editor} uploading={uploading} onImage={() => fileInput.current?.click()} onLink={openLink} />
        <EditorContent editor={editor} />
        <input
          ref={fileInput}
          type="file"
          accept={IMAGE_ACCEPT}
          hidden
          data-testid="rte-image-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void insertImage(file);
          }}
        />
      </div>
      {error ? (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {/* Mounted only while open: a closed Cloudscape Modal stays in the DOM, and its Cancel would
          sit beside the page's own. */}
      {linkOpen ? (
      <Modal
        visible
        onDismiss={() => setLinkOpen(false)}
        header="Link"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setLinkOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={applyLink}>
                {linkText.trim() ? 'Apply' : 'Remove link'}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <FormField label="URL" description="Leave empty to remove the link.">
          <Input
            value={linkText}
            placeholder="https://"
            onChange={({ detail }) => setLinkText(detail.value)}
            onKeyDown={({ detail }) => {
              if (detail.key === 'Enter') applyLink();
            }}
          />
        </FormField>
      </Modal>
      ) : null}
    </SpaceBetween>
  );
}
